import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft, CheckCircle2, Clock, Eye, Loader2, FileText, AlertCircle, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { FIELD_LABELS_ET, VALUE_LABELS_ET } from "./constants";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ThyroidDoctorView({ token, userName }) {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [draftResult, setDraftResult] = useState("");
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [waitingForAI, setWaitingForAI] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = tab === "pending" ? "/api/thyroid/doctor/pending" : "/api/thyroid/doctor/approved";
      const res = await fetch(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setItems(data.items || []);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  }, [tab, token]);

  useEffect(() => { if (!selected) fetchItems(); }, [tab, fetchItems, refreshTick, selected]);

  useEffect(() => {
    if (selected || tab !== "pending") return;
    const id = setInterval(() => setRefreshTick(n => n + 1), 15000);
    return () => clearInterval(id);
  }, [selected, tab]);

  const openCase = async (id) => {
    setSelectedLoading(true);
    setError("");
    setWaitingForAI(false);
    try {
      const res = await fetch(`${API}/api/thyroid/diagnosis/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setSelected(data);
      setDraftResult(data.result || "");
      if (!data.result && !data.doctor_approved_at) {
        setWaitingForAI(true);
        pollAIResult(id);
      }
    } catch {
      setError("Andmete laadimine ebaõnnestus");
    }
    setSelectedLoading(false);
  };

  const pollAIResult = (id) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API}/api/thyroid/diagnosis/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.result) {
          setSelected(data);
          setDraftResult(data.result);
          setWaitingForAI(false);
          clearInterval(interval);
        } else if (attempts >= 60) {
          setWaitingForAI(false);
          clearInterval(interval);
        }
      } catch (_e) { /* ignore */ }
    }, 5000);
  };

  const handleApprove = async () => {
    if (!draftResult.trim()) { setError("Raport ei tohi olla tühi."); return; }
    setApproving(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/thyroid/doctor/approve/${selected.diagnosis_id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ result: draftResult.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Viga"); }
      setSelected(null);
      setDraftResult("");
      setTab("approved");
      setRefreshTick(n => n + 1);
    } catch (err) {
      setError(err.message);
    }
    setApproving(false);
  };

  const authFetch = useCallback((url, opts = {}) => {
    return fetch(url, { ...opts, headers: { ...opts.headers, Authorization: `Bearer ${token}` } });
  }, [token]);

  const handlePdf = async (action) => {
    if (!selected) return;
    try {
      toast.info(action === "print" ? "Genereerin..." : "Laen alla...");
      const res = await authFetch(`${API}/api/thyroid/diagnoses/${selected.diagnosis_id}/pdf`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (action === "print") {
        const w = window.open(url);
        if (w) w.onload = () => w.print();
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Kilpnaare_raport_${selected.diagnosis_id.slice(-6)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error("PDF genereerimine ebaõnnestus");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  // ====== CASE DETAIL ======
  if (selected || selectedLoading) {
    if (selectedLoading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#00658D" }} /></div>;

    const q = selected.questionnaire || {};
    const tests = selected.tests || {};

    return (
      <div className="max-w-5xl mx-auto" data-testid="doctor-view-detail">
        <Button variant="outline" onClick={() => { setSelected(null); setDraftResult(""); setError(""); }} className="rounded-full mb-6" data-testid="doctor-view-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Tagasi
        </Button>

        {/* Case header */}
        <div className="rounded-3xl p-6 mb-6" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-bold" style={{ color: "#181c20" }}>
                {selected.patient_first_name && (
                  <span data-testid="doctor-detail-patient-name" style={{ color: "#00658D" }}>
                    {selected.patient_first_name} {selected.patient_last_name} · 
                  </span>
                )}
                {selected.patient_id_code && (
                  <span className="text-base font-normal" style={{ color: "#3e4850" }}> IK: {selected.patient_id_code} · </span>
                )}
                Patsient: {q.age ? `${q.age}a` : "-"}{q.gender ? `, ${q.gender}` : ""}
              </h2>
              <p className="text-sm mt-1" style={{ color: "#6e7881" }}>
                Sisestas: {selected.submitted_by_name} · {formatDate(selected.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selected.result && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => handlePdf("print")} data-testid="thyroid-print-btn" title="Prindi">
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handlePdf("download")} data-testid="thyroid-download-btn" title="Lae alla">
                    <Download className="w-4 h-4" />
                  </Button>
                </>
              )}
              {selected.doctor_approved_at ? (
                <Badge style={{ background: "rgba(16,185,129,0.12)", color: "#059669", border: "none" }}>
                  <CheckCircle2 className="w-3 h-3 mr-1" />Kinnitatud: {formatDate(selected.doctor_approved_at)}
                </Badge>
              ) : (
                <Badge style={{ background: "rgba(251,191,36,0.15)", color: "#92400e", border: "none" }}>
                  <Clock className="w-3 h-3 mr-1" />Ootab arsti kinnitust
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Raw data */}
          <div className="rounded-3xl p-6" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: "#00658D" }}>
              <FileText className="w-4 h-4" />Õe sisestatud andmed
            </h3>
            {selected.complaints && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-2" style={{ color: "#3e4850" }}>Kaebused</h4>
                <p className="text-sm p-3 rounded-xl" style={{ background: "#f7f9fe", color: "#181c20" }}>{selected.complaints}</p>
              </div>
            )}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-2" style={{ color: "#3e4850" }}>Laborianalüüsid</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {Object.entries(tests).map(([k, v]) => v !== null && v !== undefined ? (
                  <div key={k} className="flex justify-between p-2 rounded" style={{ background: "#f7f9fe" }}>
                    <span style={{ color: "#6e7881" }}>{k.toUpperCase()}</span>
                    <span className="font-medium" style={{ color: "#181c20" }}>{v}</span>
                  </div>
                ) : null)}
              </div>
            </div>
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-2" style={{ color: "#3e4850" }}>Küsimustik</h4>
              <div className="text-sm grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {Object.entries(q).filter(([k, v]) => k !== "complaints" && v !== "" && v !== null && v !== undefined).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 p-2 rounded" style={{ background: "#f7f9fe" }}>
                    <span style={{ color: "#6e7881" }}>{FIELD_LABELS_ET[k] || k}</span>
                    <span className="font-medium text-right" style={{ color: "#181c20" }}>{VALUE_LABELS_ET[String(v)] || String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
            {selected.ultrasound_text && (
              <div>
                <h4 className="text-sm font-semibold mb-2" style={{ color: "#3e4850" }}>Ultraheli</h4>
                <p className="text-sm p-3 rounded-xl whitespace-pre-wrap" style={{ background: "#f7f9fe", color: "#181c20" }}>{selected.ultrasound_text}</p>
              </div>
            )}
          </div>

          {/* AI draft / report */}
          <div className="rounded-3xl p-6" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
            <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ color: "#00658D" }}>
              <FileText className="w-4 h-4" />
              {selected.doctor_approved_at ? "Lõplik raport" : "AI mustand — muutke ja kinnitage"}
            </h3>
            <p className="text-xs mb-4" style={{ color: "#6e7881" }}>
              {selected.doctor_approved_at
                ? `Kinnitas: ${selected.doctor_approved_by_name} · ${formatDate(selected.doctor_approved_at)}`
                : "See tekst on AI ettepanek. Iga rida on muudetav. Kinnitades saadate lõpliku raporti õele."}
            </p>
            {waitingForAI ? (
              <div className="py-12 text-center rounded-xl" style={{ background: "#f7f9fe" }}>
                <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" style={{ color: "#00658D" }} />
                <p className="text-sm" style={{ color: "#6e7881" }}>AI koostab mustandit... See võib võtta kuni 1 minuti.</p>
              </div>
            ) : (
              <>
                <Textarea value={draftResult} onChange={e => setDraftResult(e.target.value)} rows={20}
                  className="rounded-xl font-mono text-sm" style={{ background: "#f7f9fe", border: "1px solid rgba(189,200,209,0.3)" }}
                  disabled={!!selected.doctor_approved_at} data-testid="doctor-draft-textarea" />
                {error && (
                  <div className="text-sm p-3 rounded-xl mt-4 flex items-start gap-2" style={{ background: "rgba(220,38,38,0.08)", color: "#991b1b" }}>
                    <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
                  </div>
                )}
                {!selected.doctor_approved_at && (
                  <Button onClick={handleApprove} disabled={approving || !draftResult.trim()}
                    className="w-full text-white h-12 rounded-full mt-4" style={{ background: "linear-gradient(180deg, #10b981 0%, #047857 100%)" }}
                    data-testid="doctor-approve-btn">
                    {approving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kinnitamine...</>) : (<><CheckCircle2 className="w-4 h-4 mr-2" />Kinnita ja saada õele</>)}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ====== LIST VIEW ======
  return (
    <div className="max-w-5xl mx-auto" data-testid="doctor-view">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-2xl font-bold" style={{ color: "#181c20" }}>Arsti töölaud</h2>
        <div className="flex gap-2">
          <Button variant={tab === "pending" ? "default" : "outline"} onClick={() => setTab("pending")} className="rounded-full"
            style={tab === "pending" ? { background: "linear-gradient(180deg, #00aeef 0%, #00658d 100%)", color: "#fff" } : {}}
            data-testid="doctor-tab-pending">
            <Clock className="w-4 h-4 mr-2" />Ootavad kinnitust
          </Button>
          <Button variant={tab === "approved" ? "default" : "outline"} onClick={() => setTab("approved")} className="rounded-full"
            style={tab === "approved" ? { background: "linear-gradient(180deg, #00aeef 0%, #00658d 100%)", color: "#fff" } : {}}
            data-testid="doctor-tab-approved">
            <CheckCircle2 className="w-4 h-4 mr-2" />Kinnitatud
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#00658D" }} /></div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl p-12 text-center" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
          <p style={{ color: "#6e7881" }}>{tab === "pending" ? "Hetkel ei ole analüüse, mis ootaksid kinnitamist." : "Kinnitatud analüüse veel ei ole."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(it => (
            <div key={it.diagnosis_id} className="rounded-2xl p-5 flex items-center justify-between hover:shadow-md transition-shadow cursor-pointer"
              style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)", border: "1px solid rgba(189,200,209,0.15)" }}
              onClick={() => openCase(it.diagnosis_id)} data-testid={`doctor-case-${it.diagnosis_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <span className="font-semibold" style={{ color: "#181c20" }}>
                    {it.patient_first_name && <span style={{ color: "#00658D" }}>{it.patient_first_name} {it.patient_last_name} · </span>}
                    Patsient: {it.patient_age ? `${it.patient_age}a` : "-"}{it.patient_gender ? `, ${it.patient_gender}` : ""}
                  </span>
                  {it.doctor_approved_at ? (
                    <Badge style={{ background: "rgba(16,185,129,0.12)", color: "#059669", border: "none" }}>
                      <CheckCircle2 className="w-3 h-3 mr-1" />{formatDate(it.doctor_approved_at)}
                    </Badge>
                  ) : (
                    <Badge style={{ background: "rgba(251,191,36,0.15)", color: "#92400e", border: "none" }}>
                      <Clock className="w-3 h-3 mr-1" />Ootab kinnitust
                    </Badge>
                  )}
                </div>
                <p className="text-xs" style={{ color: "#6e7881" }}>Sisestas: {it.submitted_by_name || "-"} · {formatDate(it.created_at)}</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-full"><Eye className="w-4 h-4 mr-2" />Vaata</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
